import {
  ApplicationRef,
  ComponentRef,
  EnvironmentInjector,
  Injectable,
  Type,
  createComponent,
  createEnvironmentInjector,
  inject
} from "@angular/core";
import { POPUP_INJECT, PopupRef } from "./popup-ref";
import { PopupComponent, PopupOptions } from "./popup-component";
import {
  PopupCreateEmptyEntityConfig,
  PopupCreateEntityConfig,
  PopupCreateEntityResolvedConfig
} from "./popup-create-entity-config";
import { CreateEntityComponent } from "../../components/ui/create-entity/create-entity.component";
import { PopupHolderComponent } from "src/app/services/popup/popup-holder/popup-holder.component";
import { PopupWizardComponent } from "src/app/services/popup/popup-wizard/popup-wizard.component";
import { PopupWizardConfig } from "src/app/services/popup/popup-wizard/popup-wizard.config";

import { sntLog } from "src/app/common/logger";

const logger = sntLog("PopupService");
@Injectable()
export class PopupService {
  private readonly appRef = inject(ApplicationRef);

  private loaderElement: HTMLDivElement | undefined;

  private readonly environmentInjector = inject(EnvironmentInjector);

  async createEmptyEntity(config: PopupCreateEmptyEntityConfig): Promise<void> {
    try {
      this.showLoader();

      await config.create();
    } catch (ex) {
    } finally {
      this.hideLoader();
    }
  }

  async createEntity<T, R = any>(
    config: PopupCreateEntityConfig<T, R>,
    mergeData?: Partial<T>
  ): Promise<void> {
    let data: R | undefined;

    if (config.resolve) {
      this.showLoader();

      try {
        data = await config.resolve();
      } catch (ex) {
        logger.error(ex);
        return;
      } finally {
        this.hideLoader();
      }
    }

    const resolvedConfig: PopupCreateEntityResolvedConfig<T, R> = {
      create: config.create,
      goToForm: config.goToForm,
      disabledValidationForGoToForm: config.disabledValidationForGoToForm,
      formItems: config.formItems,
      title: config.title,
      resolved: data,
      modalConfig: config.modalConfig,
      customActions: config.customActions,
      customActionsFactory: config.customActionsFactory,
      customActionsFactoryDataField: config.customActionsFactoryDataField,
      secondaryActions: config.secondaryActions,
      mergeData: mergeData,
      initialFormData: {
        ...config.initialFormData,
        ...(data || {})
      },
      onFormDataChange: config.onFormDataChange,
      onFormReady: config.onFormReady,
      onCancel: config.onCancel,
      confirmText: config.confirmText,
      confirmIcon: config.confirmIcon,
      rejectText: config.rejectText,
      rejectIcon: config.rejectIcon,
      rejectOnLeft: config.rejectOnLeft,
      elementFocus: config.elementFocus,
      enableMultipleCreate: config.enableMultipleCreate,
      multipleCreateLabel: config.multipleCreateLabel
    };

    await this.show(
      CreateEntityComponent,
      resolvedConfig,
      resolvedConfig.modalConfig
        ? {
            width: resolvedConfig.modalConfig.width!,
            height: resolvedConfig.modalConfig.height!,
            icon: resolvedConfig.modalConfig.icon,
            dialog: resolvedConfig.modalConfig.dialog,
            confirmOnDirtyClose: resolvedConfig.modalConfig.confirmOnDirtyClose,
            zIndex: resolvedConfig.modalConfig.zIndex,
            attachToBody: resolvedConfig.modalConfig.attachToBody
          }
        : undefined
    );
  }

  async wizard<T = any>({
    state,
    steps,
    onCreate,
    popupOptions
  }: PopupWizardConfig<T>): Promise<void> {
    await this.show(
      PopupWizardComponent,
      { state, onCreate, steps },
      popupOptions
    );
  }

  show<T, K = any>(
    component: Type<PopupComponent>,
    data?: K,
    options?: PopupOptions
  ): Promise<T | undefined> {
    return new Promise((res) => {
      let componentRef: ComponentRef<PopupHolderComponent> | undefined;

      const popupRef: PopupRef<any> = {
        confirm: async (result?: any) => {
          componentRef?.destroy();
          res(result);
        },
        close: async (result?: any) => {
          if (componentRef?.instance.canClose) {
            const canClose = await componentRef.instance.canClose();
            if (!canClose) {
              return;
            }
          }

          (<any>data)?.onCancel && (<any>data)?.onCancel();

          componentRef?.destroy();
          res(result);
        }
      };

      const newEnvInjector = createEnvironmentInjector(
        [
          { provide: POPUP_INJECT.REF, useValue: popupRef },
          {
            provide: POPUP_INJECT.COMPONENT,
            useValue: component
          },
          { provide: POPUP_INJECT.CONFIG, useValue: options },
          { provide: POPUP_INJECT.DATA, useValue: data }
        ],
        options?.environmentInjector || this.environmentInjector
      );

      componentRef = createComponent(PopupHolderComponent, {
        environmentInjector: newEnvInjector
      });

      if (!options?.attachToBody) {
        document
          .querySelector("#snoots-popups-minimized-container")
          ?.append(componentRef.location.nativeElement);
      } else {
        document.body.append(componentRef.location.nativeElement);
      }

      this.appRef.attachView(componentRef.hostView);

      if (options?.detectChangesAfterAttachView) {
        componentRef.changeDetectorRef.detectChanges();
        componentRef.hostView.detectChanges();
      }
    });
  }

  private showLoader(): void {
    const overlay: HTMLDivElement = document.createElement("div");
    overlay.classList.add(
      "absolute",
      "inset-0",
      "bg-slate-900/25",
      "flex",
      "items-center",
      "justify-center",
      "z-50"
    );

    overlay.innerHTML = `<sl-spinner style="font-size: 3rem; --indicator-color: #f9fafb; --track-color: #334155;"></sl-spinner>`;

    document.body.appendChild(overlay);

    this.loaderElement = overlay;
  }

  private hideLoader(): void {
    if (this.loaderElement) {
      if (!!this.loaderElement.parentElement) {
        document.body.removeChild(this.loaderElement!);
      }
      this.loaderElement = undefined;
    }
  }
}
